/** @jsxImportSource react */
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as d3 from "d3";
import { matchPartyByName } from "../../shared/lib/party-mappings";
import {
  PROVINCE_NAMES,
  DIPUTADOS_POR_PROVINCIA,
  renovaSenadores,
} from "../lib/province-data";

type ElectionMode = "DIPUTADOS" | "SENADORES";

interface PartyResult {
  nombre: string;
  porcentaje: number;
  bancas: number;
}

// Helper function to get party color
function getPartyColor(partyName: string): string {
  const matched = matchPartyByName(partyName);
  return matched?.color || "#808080"; // Default gray if not matched
}

interface ProvinceData {
  DIPUTADOS?: PartyResult[];
  SENADORES?: PartyResult[];
  estadoRecuento?: {
    mesasTotalizadasPorcentaje: number;
  };
  fechaTotalizacion?: string;
}

interface ElectionData {
  [provinceName: string]: ProvinceData;
}

async function fetchElectionResults(): Promise<ElectionData> {
  const response = await fetch(
    "https://elecciones2025.visualizando.ar/api/results",
  );
  if (!response.ok) {
    throw new Error("Failed to fetch election results");
  }
  return response.json();
}

export function VerticalElectionChart() {
  const [selectedProvince, setSelectedProvince] = useState("BUENOS AIRES");
  const [mode, setMode] = useState<ElectionMode>("DIPUTADOS");
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch election data with React Query
  const { data: electionData, isLoading } = useQuery({
    queryKey: ["election-results"],
    queryFn: fetchElectionResults,
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  // Get available provinces from the data
  const availableProvinces = Array.from(PROVINCE_NAMES.keys()).filter((key) => {
    if (!electionData || !electionData[key]) return false;
    const provinceData = electionData[key];
    return (
      (provinceData.DIPUTADOS && provinceData.DIPUTADOS.length > 0) ||
      (provinceData.SENADORES && provinceData.SENADORES.length > 0)
    );
  });

  // Check if senadores is available for selected province
  const senadoresAvailable =
    electionData?.[selectedProvince]?.SENADORES &&
    electionData[selectedProvince].SENADORES!.length > 0;

  // Auto-switch to DIPUTADOS if SENADORES is not available
  useEffect(() => {
    if (mode === "SENADORES" && !senadoresAvailable) {
      setMode("DIPUTADOS");
    }
  }, [selectedProvince, senadoresAvailable, mode]);

  // Get current province data
  const provinceData = electionData?.[selectedProvince];
  const parties = provinceData?.[mode] || [];
  const estadoRecuento = provinceData?.estadoRecuento;
  const fechaTotalizacion = provinceData?.fechaTotalizacion;

  // Calculate total bancas
  const totalBancas =
    mode === "DIPUTADOS"
      ? DIPUTADOS_POR_PROVINCIA.get(selectedProvince)?.renuevan || 0
      : renovaSenadores(selectedProvince)
        ? 3
        : 0;

  // Calculate max percentage for scaling
  const maxPercentage =
    parties.length > 0 ? Math.max(...parties.map((p) => p.porcentaje)) : 100;

  // D3 rendering
  useEffect(() => {
    if (!svgRef.current || !containerRef.current || parties.length === 0)
      return;

    const svg = d3.select(svgRef.current);

    // Clear previous content
    svg.selectAll("*").remove();

    // Dimensions
    const margin = { top: 20, right: 20, bottom: 40, left: 20 };
    const width = containerRef.current.clientWidth - margin.left - margin.right;
    const height = parties.length * 60; // 60px per party

    svg.attr("width", width + margin.left + margin.right);
    svg.attr("height", height + margin.top + margin.bottom);

    const g = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Scales
    const xScale = d3
      .scaleLinear()
      .domain([0, maxPercentage])
      .range([0, width]);

    const yScale = d3
      .scaleBand()
      .domain(parties.map((_, i) => i.toString()))
      .range([0, height])
      .padding(0.2);

    // Draw bars
    const bars = g
      .selectAll(".bar-group")
      .data(parties)
      .enter()
      .append("g")
      .attr("class", "bar-group")
      .attr("transform", (_d, i) => `translate(0,${yScale(i.toString())})`);

    // Background bar
    bars
      .append("rect")
      .attr("class", "bar-bg")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", 0)
      .attr("height", yScale.bandwidth())
      .attr("fill", (d) => getPartyColor(d.nombre) + "33")
      .attr("rx", 4)
      .transition()
      .duration(600)
      .attr("width", width);

    // Foreground bar (percentage)
    bars
      .append("rect")
      .attr("class", "bar-fg")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", 0)
      .attr("height", yScale.bandwidth())
      .attr("fill", (d) => getPartyColor(d.nombre))
      .attr("rx", 4)
      .transition()
      .duration(800)
      .attr("width", (d) => xScale(d.porcentaje));

    // Party name label (inside bar, left side, upper part)
    bars
      .append("text")
      .attr("class", "party-label")
      .attr("x", 10)
      .attr("y", yScale.bandwidth() / 2 - 5)
      .attr("text-anchor", "start")
      .attr("dominant-baseline", "middle")
      .style("font-size", "13px")
      .style("font-weight", "700")
      .style("fill", "#fff")
      .style("text-shadow", "1px 1px 3px rgba(0,0,0,0.6)")
      .text((d) => d.nombre);

    // Percentage label (inside bar, right side)
    bars
      .append("text")
      .attr("class", "percentage-label")
      .attr("x", (d) => Math.max(80, xScale(d.porcentaje) - 10))
      .attr("y", yScale.bandwidth() / 2)
      .attr("text-anchor", "end")
      .attr("dominant-baseline", "middle")
      .style("font-size", "22px")
      .style("font-weight", "700")
      .style("fill", "#fff")
      .style("text-shadow", "1px 1px 3px rgba(0,0,0,0.6)")
      .text((d) => `${d.porcentaje.toFixed(1)}%`);

    // Seat indicators (circles below party name) - only show if bancas > 0
    bars.each(function (d) {
      if (d.bancas === 0) return; // Skip if no bancas

      const barGroup = d3.select(this);

      // Add circles
      barGroup
        .selectAll(".seat-circle")
        .data(d3.range(d.bancas))
        .enter()
        .append("circle")
        .attr("class", "seat-circle")
        .attr("cx", (_, ci) => 15 + ci * 12)
        .attr("cy", yScale.bandwidth() / 2 + 12)
        .attr("r", 4)
        .attr("fill", "#fff")
        .attr("stroke", "#fff")
        .attr("stroke-width", 1)
        .style("opacity", 0)
        .transition()
        .duration(300)
        .delay((_, ci) => ci * 50)
        .style("opacity", 1);

      // Add bancas count next to dots
      barGroup
        .append("text")
        .attr("class", "bancas-count")
        .attr("x", 15 + d.bancas * 12 + 5)
        .attr("y", yScale.bandwidth() / 2 + 12)
        .attr("text-anchor", "start")
        .attr("dominant-baseline", "middle")
        .style("font-size", "11px")
        .style("font-weight", "600")
        .style("fill", "#fff")
        .style("text-shadow", "1px 1px 2px rgba(0,0,0,0.5)")
        .style("opacity", 0)
        .text(`${d.bancas}`)
        .transition()
        .duration(300)
        .delay(d.bancas * 50)
        .style("opacity", 1);
    });
  }, [parties, maxPercentage]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-lg text-slate-600">Cargando resultados...</div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden w-full">
      {/* Controls Header */}
      <div
        className="shrink-0 border-b-4 border-white px-5 py-3"
        style={{ backgroundColor: "#006AC8" }}
      >
        <div className="flex flex-wrap items-center justify-center gap-4">
          {/* Province selector */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-white">
              Provincia:
            </span>
            <select
              value={selectedProvince}
              onChange={(e) => setSelectedProvince(e.target.value)}
              className="rounded border-2 border-white bg-[#444] px-2 py-1 text-xs font-medium text-white"
              style={{ minWidth: "140px" }}
            >
              {availableProvinces.map((key) => (
                <option key={key} value={key}>
                  {PROVINCE_NAMES.get(key)}
                </option>
              ))}
            </select>
          </div>

          {/* Mode toggle */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-white">
              Elección:
            </span>
            <div className="flex gap-1 rounded border-2 border-white bg-[#444] p-0.5">
              <button
                onClick={() => setMode("DIPUTADOS")}
                className={`rounded px-3 py-1 text-xs font-medium transition-all ${
                  mode === "DIPUTADOS"
                    ? "bg-white text-[#444]"
                    : "bg-transparent text-white"
                }`}
              >
                DIPUTADOS
              </button>
              <button
                onClick={() => setMode("SENADORES")}
                disabled={!senadoresAvailable}
                className={`rounded px-3 py-1 text-xs font-medium transition-all ${
                  mode === "SENADORES" && senadoresAvailable
                    ? "bg-white text-[#444]"
                    : "bg-transparent text-white"
                } ${!senadoresAvailable ? "cursor-not-allowed opacity-30" : ""}`}
              >
                SENADORES
              </button>
            </div>
          </div>

          {/* Bancas en juego */}
          <div className="text-xs font-medium uppercase tracking-wide text-white">
            {totalBancas} bancas en juego
          </div>

          {/* Escrutinio progress */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium uppercase text-white">
              Escrutinio:
            </span>
            <div
              className="h-3 w-20 overflow-hidden rounded-sm"
              style={{ backgroundColor: "rgba(255, 255, 255, 0.2)" }}
            >
              <div
                className="h-full bg-[#ddd] transition-all duration-600"
                style={{
                  width: `${estadoRecuento?.mesasTotalizadasPorcentaje || 0}%`,
                }}
              />
            </div>
            <span className="text-xs font-bold text-white">
              {Math.floor(estadoRecuento?.mesasTotalizadasPorcentaje || 0)}%
            </span>
          </div>

          {/* Timestamp */}
          {fechaTotalizacion && (
            <div className="ml-auto text-xs text-white/80">
              Actualizado:{" "}
              {new Date(fechaTotalizacion).toLocaleTimeString("es-AR", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
          )}
        </div>
      </div>

      {/* Chart Area */}
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto bg-white p-4"
        style={{ minHeight: 0 }}
      >
        {parties.length > 0 ? (
          <svg ref={svgRef} className="w-full" />
        ) : (
          <div className="flex h-full items-center justify-center">
            <div className="text-center text-slate-500">
              No hay datos disponibles para esta selección
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
